import { CHECKOUT_ITEM_TYPE_FLIGHT } from 'constants/checkout'
import uuidV4 from 'lib/string/uuidV4Utils'
import { FlightsClassTypes, FlightSegment, FlightViewTypes } from 'constants/flight'
import { last } from 'lib/array/arrayUtils'

export function getJourneyFields(
  journey: App.JourneyV2,
) {
  return {
    journeyId: journey.id,
    journeyKey: journey.bookingInfo.journeyKey,
    provider: journey.provider,
    providerSearchId: journey.bookingInfo.providerSearchId,
    cost: journey.price.all.totalFare,
    carrierImage: {
      url: journey.carrierLogo,
      title: journey.carrierName,
    },
  }
}

function getViewType(
  departing?: App.AnyJourney,
  returning?: App.AnyJourney,
  viewType?: string,
) {
  const { RETURN, TWO_ONE_WAYS_AND_RETURN } = FlightViewTypes
  const isReturnCombinedFlight = departing?.fareType === RETURN && returning?.fareType === RETURN
  return isReturnCombinedFlight ? TWO_ONE_WAYS_AND_RETURN : viewType
}

interface FlightItemUpdate {
  originAirportCode?: string;
  destinationAirportCode?: string;
  searchId?: string;
  forceBundleId?: string;
}

export const EmptyBaggage: App.Checkout.FlightItemPaxBaggageMap = { baggage: {}, carryOnBaggage: {} }

export function setFlightCheckoutItemJourney(
  checkoutItem: App.Checkout.FlightItem,
  journey: App.JourneyV2,
  segment: 'departing' | 'returning',
  expiry: number,
  options: {
    fareFamily?: App.FlightFareFamily;
    baggage?: App.Checkout.FlightItemPaxBaggageMap;
    forceBundleId?: string;
    selectedCabinClass?: FlightsClassTypes;
    areAllFlightsCovered?: boolean;
  },
) {
  const journeyFields = getJourneyFields(journey)

  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    selectedCabinClass: options.selectedCabinClass,
    expiry,
    // our flight is changing, reset the fare fetching behaviour
    hasFinalFare: false,
    fareFetchError: undefined,
    journeyType: journey.itemType,
    forceBundleId: options.forceBundleId ?? checkoutItem.forceBundleId,
    totalFare: 0,
    searchId: checkoutItem.searchId,
    viewType: journey.fareType === FlightViewTypes.RETURN ? FlightViewTypes.TWO_ONE_WAYS_AND_RETURN : checkoutItem.viewType,
    flights: [FlightSegment.DEPARTING, FlightSegment.RETURNING].map((currentSegment) => {
      const flight = currentSegment === FlightSegment.DEPARTING ? checkoutItem.flights[0] : checkoutItem.flights[1]

      if (segment === currentSegment) {
        const firstFlight = journey.flightGroup.flights[0]
        const lastFlight = last(journey.flightGroup.flights)

        return {
          ...journeyFields,
          carrier: firstFlight?.marketingAirline.code,
          extras: options.baggage ?? EmptyBaggage,
          fareFamily: options.fareFamily || flight?.fareFamily,
          departingAirportCode: firstFlight?.departureAirport,
          arrivalAirportCode: lastFlight?.arrivalAirport,
          departingDate: firstFlight?.departingDate,
          arrivalAirportName: lastFlight?.arrivalAirportName,
          departingAirportName: firstFlight?.departureAirportName,
          arrivalDateTime: lastFlight?.arrivalDateTime,
          departureDateTime: firstFlight?.departureDateTime,
          duration: journey.flightGroup.totalFlightDuration,
          numberOfStops: journey.flightGroup.flights.length - 1,
          ...(options.areAllFlightsCovered ? { withEmbeddedInsurance: true } : {}),
        }
      }

      if (options.areAllFlightsCovered) {
        flight.withEmbeddedInsurance = true
      }

      return flight
    }).filter(Boolean),
  }

  return updatedItem
}

export function resetFlightCheckoutItem(
  checkoutItem: App.Checkout.FlightItem,
) {
  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    flights: [],
  }

  return updatedItem
}

export function setFlightCheckoutItemJourneyFareFamily(
  checkoutItem: App.Checkout.FlightItem,
  flightIndex: number,
  options: {
    fareFamily?: App.FlightFareFamily;
  },
) {
  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    flights: checkoutItem.flights.map((flight, index) => {
      if (index === flightIndex) {
        return {
          ...flight,
          fareFamily: options.fareFamily,
        }
      }

      return flight
    }),
  }

  return updatedItem
}

export function updateFlightCheckoutItem(
  checkoutItem: App.Checkout.FlightItem,
  updates: FlightItemUpdate,
): App.Checkout.FlightItem {
  const {
    originAirportCode,
    destinationAirportCode,
    searchId,
    forceBundleId,
  } = updates

  const flights = searchId !== checkoutItem.searchId ? [] : checkoutItem.flights

  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    originAirportCode: originAirportCode ?? checkoutItem.originAirportCode,
    destinationAirportCode: destinationAirportCode ?? checkoutItem.destinationAirportCode,
    searchId,
    forceBundleId,
    // we just updated the item, fare may have changed
    hasFinalFare: false,
    fareFetchError: undefined,
    flights,
  }

  return updatedItem
}

/**
 * Get airport codes from actual flight journey data.
 *
 * - One-way flights (flights.length === 1, MEL -> SYD): uses the first flight's departing and arrival airport codes
 * - Return flights (flights.length === 2, MEL -> SYD, SYD -> MEL): uses the first flight's departing and arrival airport codes
 * - Multi-city flights (flights.length >= 2, MEL -> SYD, SYD -> BNE): uses the first flight's departing and last flight's arrival airport codes
 */
function getFlightJourneyAirportCode(
  fareType: GenerateItemFields['fareType'],
  flights: Array<App.Checkout.FlightItemFlight>,
) {
  switch (fareType) {
    case 'oneWay':
    case 'return':
      return { originAirportCode: flights[0].departingAirportCode, destinationAirportCode: flights[0].arrivalAirportCode }
    default:
      return { originAirportCode: flights[0].departingAirportCode, destinationAirportCode: last(flights).arrivalAirportCode }
  }
}

interface GenerateItemFields {
  originAirportCode: string,
  destinationAirportCode: string,
  occupants: App.Occupants,
  passengers: Array<App.Checkout.FlightPassenger>,
  fareType: 'return' | 'oneWay' | 'multiCity',
  viewType: string,
  searchId?: string,
  isFlightCredit?: boolean,
  itemId?: string,
  fareFamilies?: Array<App.FlightFareFamily | undefined>,
  baggage?: Array<App.Checkout.FlightItemPaxBaggageMap>,
  flights?: Array<App.JourneyV2>,
  bundledItemIds?: Array<string>,
  quotedFare?: number;
  expiry?: number,
  selectedCabinClass?: FlightsClassTypes,
  areAllFlightsCovered?: boolean,
}

export function generateFlightCheckoutItem(fields: GenerateItemFields): App.Checkout.FlightItem {
  const {
    searchId,
    flights = [],
    baggage = [],
    fareFamilies = [],
    occupants,
    passengers,
    fareType,
    viewType,
    isFlightCredit,
    itemId,
    bundledItemIds,
    originAirportCode,
    destinationAirportCode,
    quotedFare,
    expiry,
    selectedCabinClass,
    areAllFlightsCovered,
  } = fields

  const flightItemFlights = flights.map((flight, index) => {
    const journey = getJourneyFields(flight)
    return {
      carrier: flight.flightGroup.flights[0].marketingAirline.code,
      journeyId: journey.journeyId,
      journeyKey: journey.journeyKey,
      provider: journey.provider,
      providerSearchId: journey.providerSearchId,
      fareFamily: fareFamilies[index],
      extras: baggage[index] ?? EmptyBaggage,
      cost: journey.cost,
      carrierImage: journey.carrierImage,
      departingAirportCode: flight.flightGroup.flights[0].departureAirport,
      arrivalAirportCode: last(flight.flightGroup.flights).arrivalAirport,
      departingDate: flight.flightGroup.flights[0].departingDate,
      arrivalAirportName: last(flight.flightGroup.flights).arrivalAirportName,
      departingAirportName: flight.flightGroup.flights[0].departureAirportName,
      arrivalDateTime: last(flight.flightGroup.flights).arrivalDateTime,
      departureDateTime: flight.flightGroup.flights[0].departureDateTime,
      duration: flight.flightGroup.totalFlightDuration,
      numberOfStops: flight.flightGroup.flights.length - 1,
      ...(areAllFlightsCovered ? { withEmbeddedInsurance: true } : {}),
    }
  })

  const flightJourneyAirportCodes = flightItemFlights.length > 0
    ? getFlightJourneyAirportCode(fareType, flightItemFlights)
    : { originAirportCode, destinationAirportCode }

  return {
    itemId: itemId ?? uuidV4(),
    transactionKey: uuidV4(),
    carrier: flights[0]?.flightGroup?.flights[0]?.marketingAirline.code,
    itemType: CHECKOUT_ITEM_TYPE_FLIGHT,
    originAirportCode: flightJourneyAirportCodes.originAirportCode,
    destinationAirportCode: flightJourneyAirportCodes.destinationAirportCode,
    passengers,
    occupants,
    fareType,
    viewType: getViewType(flights[0], flights[1], viewType),
    quotedFare,
    journeyType: flights[0]?.itemType,
    isFlightCredit: !!isFlightCredit,
    searchId,
    bundledItemIds,
    expiry,
    selectedCabinClass,
    flights: flightItemFlights,
    hasFinalFare: false,
  }
}
